import { BankAccountDetails, JaneGoldLearnMoreLink } from '@iheartjane/menu';
import {
  Accordion,
  Box,
  Button,
  Flex,
  JaneGoldIcon,
  Skeleton,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import isUndefined from 'lodash-es/isUndefined';
import { useEffect, useState } from 'react';

import { useJaneGoldData } from '@jane/shared-ecomm/hooks';
import { getJaneGoldFormattedLabel } from '@jane/shared/util';

import type { JaneGoldCartCalloutProps } from '../janeGoldCallout.types';
import { JaneGoldFeeBreakdown } from '../janeGoldFeeBreakdown/janeGoldFeeBreakdown';
import { JaneGoldTermsLink } from '../janeGoldTermsLink/janeGoldTermsLink';
import { CartBrandSpecialCallout } from './cartBrandSpecialCallout';
import styles from './cartBrandSpecialCallout.module.css';
import {
  StyledCartButtonWrapper,
  StyledCartContainer,
  StyledCartDescriptionWrapper,
  StyledCartInnerContainer,
} from './janeGoldCartCallout.styles';

/**
 * Cart variant of the Jane Gold callout action component. If the user doesn't have a linked bank, it will display a `Learn more` link and `Link bank account` button. If the user has a linked bank, it will list the linked bank information and hide the `Link bank account` button.
 */
export const JaneGoldCartCallout = ({
  bundlesInProgress,
  buttonLabel,
  cartBrandDiscounts,
  cartUuid,
  description,
  discountLabel,
  feeLabel,
  hasBreakdown,
  headlessLoginUrl,
  onBrandSpecialClick,
  onLinkAccountClick,
  storeId,
  totalLabel,
  title,
}: JaneGoldCartCalloutProps) => {
  const [hasBundlesInProgress, setHasBundlesInProgress] = useState(
    bundlesInProgress ? bundlesInProgress?.length > 0 : false
  );
  const { isGuestUser, isLoading, selectedBankAccount, userHasLinkedBank } =
    useJaneGoldData();

  const isMobile = useMobileMediaQuery({});
  const formattedLabel =
    typeof discountLabel === 'number'
      ? getJaneGoldFormattedLabel(discountLabel)
      : discountLabel;
  const shouldShowHeadlessLogin = isGuestUser && headlessLoginUrl;

  const parentDocument =
    /* eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc */
    document.getElementById('shadow-host')?.shadowRoot || document;
  const numberOfBundlesInProgress = parentDocument.querySelector(
    '#bundles-in-progress-list'
  )?.childElementCount;

  const hideFormattedLabel = hasBundlesInProgress && !hasBreakdown;

  const defaultClosed = !hasBundlesInProgress && userHasLinkedBank;

  // we reassert if there are actual bundles in progress by checking dom elements since
  // they can return null if the downstream check of their qualifying status returns 'applied'
  // but use the bundlesInProgress array as default since accordion content doesn't
  // exist in the dom when it is closed
  useEffect(() => {
    if (!isUndefined(numberOfBundlesInProgress)) {
      setHasBundlesInProgress(numberOfBundlesInProgress > 0);
    }
  }, [numberOfBundlesInProgress]);

  return (
    <Box background="grays-white">
      {hasBreakdown || hasBundlesInProgress ? (
        <Accordion.Item id="jane-gold" defaultExpanded={!defaultClosed}>
          <Accordion.Heading px={isMobile ? 24 : 40} py={20}>
            <Flex alignItems="center">
              <Flex alignItems="center">
                <div>
                  <JaneGoldIcon mr={16} />
                </div>
                <Typography variant="body-bold">{title}</Typography>
              </Flex>
              <Flex ml="auto">
                {!hideFormattedLabel && (
                  <Typography variant="body-bold" mx={16}>
                    {formattedLabel}
                  </Typography>
                )}
                <Accordion.Indicator iconSize="md" />
              </Flex>
            </Flex>
          </Accordion.Heading>
          <Accordion.Content px={isMobile ? 24 : 40} pb={24} destroyOnClose>
            <Box pl={40} className={styles['bundlesInProgress']}>
              <Typography
                id="bundles-in-progress-title"
                className={styles['bundlesInProgressTitle']}
              >{`${
                !numberOfBundlesInProgress
                  ? 'Bundles'
                  : pluralize({
                      number: numberOfBundlesInProgress,
                      noun: 'bundle',
                    })
              } started`}</Typography>
              <Flex
                as="ul"
                py={8}
                gap={16}
                flexDirection="column"
                aria-labelledby="bundles-in-progress-title"
                id="bundles-in-progress-list"
              >
                {bundlesInProgress?.map((brandSpecial) => (
                  <CartBrandSpecialCallout
                    key={brandSpecial.id}
                    brandSpecial={brandSpecial}
                    cartBrandDiscounts={cartBrandDiscounts}
                    cartUuid={cartUuid}
                    isAuthenticated={!isGuestUser}
                    onBrandSpecialClick={onBrandSpecialClick}
                    storeId={storeId}
                  />
                ))}
              </Flex>
            </Box>

            {hasBreakdown && (
              <Box pl={40} pt={hasBundlesInProgress ? 8 : 0}>
                <JaneGoldFeeBreakdown
                  buttonLabel={buttonLabel}
                  cartBrandDiscounts={cartBrandDiscounts}
                  description={description}
                  discountLabel={discountLabel}
                  feeLabel={feeLabel}
                  onLinkAccountClick={onLinkAccountClick}
                  totalLabel={totalLabel}
                  variant="cart"
                />
              </Box>
            )}
          </Accordion.Content>
        </Accordion.Item>
      ) : (
        <StyledCartContainer>
          <Flex
            mb={20}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Flex alignItems="center">
              <JaneGoldIcon mr={16} altText="jane-gold-icon" size="md" />
              <Typography variant="body-bold">{title}</Typography>
            </Flex>
            <Typography variant="body-bold">{formattedLabel}</Typography>
          </Flex>

          <StyledCartInnerContainer>
            <StyledCartDescriptionWrapper userHasLinkedBank={userHasLinkedBank}>
              {!isLoading && (
                <>
                  <Typography variant="body" as="span">
                    {description}
                    &nbsp;
                    {!userHasLinkedBank ? (
                      <JaneGoldTermsLink label="Learn more" />
                    ) : (
                      <JaneGoldLearnMoreLink userHasLinkedBank />
                    )}
                  </Typography>
                  {userHasLinkedBank && selectedBankAccount && (
                    <BankAccountDetails
                      bankAccount={selectedBankAccount}
                      key={selectedBankAccount.bank_account_id}
                      mt={24}
                    />
                  )}
                </>
              )}

              {isLoading && (
                <Box>
                  <Skeleton animate>
                    <Skeleton.Bone height="45px" />
                    <Skeleton.Bone height="96px" mt={24} />
                  </Skeleton>
                </Box>
              )}
            </StyledCartDescriptionWrapper>

            {!isLoading && shouldShowHeadlessLogin && (
              <StyledCartButtonWrapper>
                <Button
                  full
                  href={headlessLoginUrl}
                  target="_parent"
                  variant="secondary"
                  label="Sign in to link bank account"
                />
              </StyledCartButtonWrapper>
            )}

            {!(isLoading || userHasLinkedBank || shouldShowHeadlessLogin) && (
              <StyledCartButtonWrapper>
                <Button
                  full
                  onClick={onLinkAccountClick}
                  variant="secondary"
                  label="Link bank account"
                />
              </StyledCartButtonWrapper>
            )}
          </StyledCartInnerContainer>
        </StyledCartContainer>
      )}
    </Box>
  );
};
